/**
 * A button that says why it is off (#262). `reason` = blocked for something
 * the user can fix: rendered `aria-disabled` with the reason on hover, click
 * ignored. `busy` = a call is in flight: native `disabled`. See
 * lib/disabledReason.ts for the rule.
 */
import type { ButtonHTMLAttributes } from 'react';
import { buttonGate } from '../lib/disabledReason';

type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled' | 'title'> & {
  busy?: boolean;
  reason?: string | null;
  /** Hover text while the button is usable. */
  hint?: string;
};

export function GatedButton({ busy, reason, hint, onClick, type = 'button', ...rest }: Props) {
  const gate = buttonGate({ busy, reason, hint });
  return (
    <button
      {...rest}
      type={type}
      disabled={gate.disabled}
      aria-disabled={gate.ariaDisabled || undefined}
      title={gate.title}
      onClick={(e) => {
        // preventDefault also stops a blocked submit button submitting its form.
        if (gate.blocked) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      }}
    />
  );
}

/** The reason as a muted line under a control, for touch where hover doesn't exist. */
export function ReasonNote({ reason }: { reason: string | null | undefined }) {
  if (!reason) return null;
  return (
    <div className="reason-note" role="note">
      {reason}
    </div>
  );
}
